import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ConfirmModal } from '../ui/Modal';
import OsScroll from '../ui/OsScroll';
import ManageWorkspaceRow from './ManageWorkspaceRow';
import WorkspaceModal from './WorkspaceModal';
import { reorderIds } from './useSwitcherDnd';
import { removeWorkspace, reorderWorkspaces, updateWorkspace } from './workspaceActions';
import './workspaces.css';

const SECONDARY = 'px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm';

/**
 * Manage workspaces (card 5.2): every workspace in the switcher's order —
 * rename inline (F2), icon & colour (the New workspace modal in edit mode),
 * delete (confirm: its documents move to Personal, none is deleted) and
 * reorder by drag or Alt+Up / Alt+Down.
 */
/** The docscope actions by default; documentation spaces (card 4.3) pass their own. */
const WORKSPACE_ACTIONS = {
  update: (id, fields) => updateWorkspace(id, fields),
  reorder: (ids) => reorderWorkspaces(ids),
  remove: (ws) => removeWorkspace(ws),
};

export default function ManageWorkspacesModal({ workspaces, onClose, actions = WORKSPACE_ACTIONS, ns = 'docSidebar.workspaces' }) {
  const { t } = useTranslation();
  const [renamingId, setRenamingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null); // { id, after }

  const ids = workspaces.map((w) => w.id);
  const move = (id, delta) => {
    const at = ids.indexOf(id);
    const to = at + delta;
    if (at < 0 || to < 0 || to >= ids.length) return;
    actions.reorder(reorderIds(ids, id, ids[to], delta > 0));
  };

  const endDrag = () => {
    setDragId(null);
    setOver(null);
  };

  const dragFor = (ws) => ({
    marker: over?.id === ws.id ? (over.after ? 'after' : 'before') : undefined,
    props: {
      draggable: renamingId !== ws.id,
      onDragStart: (e) => {
        setDragId(ws.id);
        try {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('application/x-apiboo-docscope', ws.id);
        } catch {
          /* jsdom */
        }
      },
      onDragOver: (e) => {
        if (!dragId || dragId === ws.id) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        if (over?.id !== ws.id || over.after !== after) setOver({ id: ws.id, after });
      },
      onDrop: (e) => {
        e.preventDefault();
        if (dragId && dragId !== ws.id) actions.reorder(reorderIds(ids, dragId, ws.id, !!over?.after));
        endDrag();
      },
      onDragEnd: endDrag,
    },
  });

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={t(`${ns}.manageTitle`)}
        subtitle={t(`${ns}.manageSubtitle`)}
      >
        <div data-testid="doc-workspaces-manage">
          {workspaces.length === 0 ? (
            <p className="doc-ws-empty">{t(`${ns}.none`)}</p>
          ) : (
            <OsScroll style={{ maxHeight: '22rem' }}>
              <ul className="doc-ws-list">
                {workspaces.map((ws) => (
                  <ManageWorkspaceRow
                    key={ws.id}
                    ws={ws}
                    renaming={renamingId === ws.id}
                    onStartRename={() => setRenamingId(ws.id)}
                    onCancelRename={() => setRenamingId(null)}
                    onRename={async (name) => {
                      setRenamingId(null);
                      await actions.update(ws.id, { name });
                    }}
                    onEdit={() => setEditing(ws)}
                    onDelete={() => setDeleting(ws)}
                    onMove={(delta) => move(ws.id, delta)}
                    drag={dragFor(ws)}
                    ns={ns}
                  />
                ))}
              </ul>
            </OsScroll>
          )}
          <div className="flex justify-end pt-4">
            <button type="button" className={SECONDARY} onClick={onClose}>
              {t(`${ns}.done`)}
            </button>
          </div>
        </div>
      </Modal>
      {editing && (
        <WorkspaceModal
          ns={ns}
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (fields) => {
            const ok = await actions.update(editing.id, fields);
            if (ok) setEditing(null);
            return ok;
          }}
        />
      )}
      {deleting && (
        <ConfirmModal
          isOpen
          type="danger"
          title={t(`${ns}.deleteTitle`, { name: deleting.name })}
          message={t(`${ns}.deleteMessage`)}
          confirmText={t(`${ns}.deleteConfirm`)}
          cancelText={t('common.cancel')}
          onConfirm={() => actions.remove(deleting)}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
